import React, { useEffect, useState } from 'react';
import { useProjectImages } from '@/hooks/useProjectImages';
import { ProjectData } from '@/types/project';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface AutoImageGeneratorProps {
  property: ProjectData;
  images: string[];
  onImagesUpdated: (newImages: string[]) => void;
}

export const AutoImageGenerator: React.FC<AutoImageGeneratorProps> = ({
  property,
  images,
  onImagesUpdated
}) => {
  const { generateProjectImages, isGenerating } = useProjectImages();
  const [hasGenerated, setHasGenerated] = useState(false);

  useEffect(() => {
    const generateMissingImages = async () => {
      // Only generate if we have no images or very few images, and haven't generated yet
      if (images.length < 3 && !hasGenerated && !isGenerating) {
        try {
          console.log(`Generating missing images for ${property.name}...`);
          setHasGenerated(true);
          
          // Try real images first, then fallback to AI
          const newImages = await generateProjectImages(property, true);
          
          if (newImages.length > 0) {
            // Combine existing images with new ones, avoiding duplicates
            const allImages = [...images, ...newImages].filter((img, index, arr) => 
              arr.indexOf(img) === index
            );
            onImagesUpdated(allImages);
            
            toast.success(`Generated ${newImages.length} images for ${property.name}`);
          }
        } catch (error) {
          console.error('Failed to generate images:', error);
          toast.error('Failed to generate images automatically');
        }
      }
    };

    // Debounce the generation to avoid multiple calls
    const timer = setTimeout(generateMissingImages, 1000);
    return () => clearTimeout(timer);
  }, [property, images.length, hasGenerated, isGenerating, generateProjectImages, onImagesUpdated]);

  if (isGenerating) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Generating property images...
      </div>
    );
  }

  return null;
};